import { useEffect, useState } from 'react';
import { exportApi, usersApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SearchableSelect } from '../components/ui/searchable-select';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, Loader2, Users } from 'lucide-react';

export default function ReportsPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedEmployee, setSelectedEmployee] = useState('');

  const years = Array.from({ length: 5 }, (_, i) => (new Date().getFullYear() - i).toString());

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await usersApi.getAll();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportAll = async () => {
    setExporting(true);
    try {
      const response = await exportApi.exportAll(parseInt(selectedYear));
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `performance_data_${selectedYear}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export completed successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleExportEmployee = async () => {
    if (!selectedEmployee) {
      toast.error('Please select an employee');
      return;
    }

    setExporting(true);
    try {
      const response = await exportApi.exportEmployee(selectedEmployee, parseInt(selectedYear));
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const employee = users.find(u => u.id === selectedEmployee);
      a.download = `${employee?.name || 'employee'}_performance_${selectedYear}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export completed successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Reports & Export</h1>
        <p className="text-muted-foreground mt-1">
          Generate and export performance data reports
        </p>
      </div>

      {/* Year Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Report Settings</CardTitle>
          <CardDescription>Select the year for your reports</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="w-48">
              <SearchableSelect
                value={selectedYear}
                onValueChange={setSelectedYear}
                placeholder="Select year"
                data-testid="year-select"
                options={years.map(year => ({ value: year, label: year }))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export All Data */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Export All Performance Data
          </CardTitle>
          <CardDescription>
            Download a comprehensive Excel file with all users, goals, and reviews
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm">
                This export includes:
              </p>
              <ul className="text-sm text-muted-foreground mt-1 list-disc list-inside">
                <li>All users and their details</li>
                <li>Goals with ratings and status</li>
                <li>Review summaries and scores</li>
              </ul>
            </div>
            <Button
              onClick={handleExportAll}
              disabled={exporting}
              data-testid="export-all-btn"
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Export All Data
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Export Individual Employee */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Export Individual Employee Report
          </CardTitle>
          <CardDescription>
            Download performance data for a specific employee
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1 max-w-md">
              <SearchableSelect
                value={selectedEmployee}
                onValueChange={setSelectedEmployee}
                placeholder="Select employee"
                searchPlaceholder="Search employees..."
                data-testid="employee-select"
                options={users.map(user => ({
                  value: user.id,
                  label: `${user.name} (${user.employee_number})`,
                }))}
              />
            </div>
            <Button
              onClick={handleExportEmployee}
              disabled={exporting || !selectedEmployee}
              data-testid="export-employee-btn"
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Export Employee Report
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Info */}
      <Card>
        <CardHeader>
          <CardTitle>Report Contents</CardTitle>
          <CardDescription>What's included in each export type</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-4 bg-muted/50 rounded-lg">
              <h4 className="font-medium mb-2">All Data Export</h4>
              <p className="text-sm text-muted-foreground mb-3">
                Multi-sheet Excel workbook containing:
              </p>
              <ul className="text-sm space-y-1">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary"></span>
                  Users Sheet - Employee directory
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary"></span>
                  Goals Sheet - All goals with details
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary"></span>
                  Reviews Sheet - Performance summaries
                </li>
              </ul>
            </div>
            <div className="p-4 bg-muted/50 rounded-lg">
              <h4 className="font-medium mb-2">Individual Export</h4>
              <p className="text-sm text-muted-foreground mb-3">
                Single employee Excel workbook containing:
              </p>
              <ul className="text-sm space-y-1">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-secondary"></span>
                  Employee Info - Personal details
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-secondary"></span>
                  Goals Sheet - Individual goals
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-secondary"></span>
                  Review Summary - Scores & feedback
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
